import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import useTitle from 'utils/customHook/useTitle'
import { getStationTitleWithTownName } from 'utils/StationUtils'
import HydrobioOperationDescription from './HydrobioOperationDescription'
import { getDate } from 'utils/DateUtil'
import { getLabel } from 'utils/StoreUtils'
import OperationAction from '../../operation/actions/OperationAction'
import MethodAction from 'referencial/components/methods/actions/MethodAction'
import QualityAction from 'quality/actions/QualityAction'
import SupportAction from 'referencial/components/support/actions/SupportAction'
import EnvironmentalConditionsPanel from '../../operation/components/environmentalCondition/EnvironmentalConditionsPanel'
import TaxonAction from 'referencial/components/taxon/actions/TaxonAction'
import useSandreList from 'utils/customHook/useSandreList'
import HydrobioOperationFaunalList from './HydrobioOperationFaunalList'
import HydrobioOperationFloristicList from './HydrobioOperationFloristicList'
import { useParams } from 'react-router'
import OperationHydrobioPressuresPanel from './OperationHydrobioPressuresPanel'
import OperationIndicesPanel from 'quality/components/operation/components/indices/OperationIndicesPanel'
import HydrobioSample from './HydrobioSample'
import HydrobioOperationSurveyUnits from './HydrobioOperationSurveyUnits'
import { Grid } from '@mui/material'
import Tabs from 'components/Tabs'
import { CODE_SUPPORT, SUPPORT_MACROPHYTES } from 'quality/constants/HydrobioConstant'

const DESCRIPTION = 'DESCRIPTION'
const SAMPLE = 'SAMPLE'
const SURVEY_UNITS = 'SURVEY_UNITS'
const FAUNAL_LIST = 'FAUNAL_LIST'
const FLORISTIC_LIST = 'FLORISTIC_LIST'
const INDICES = 'INDICES'
const PRESSURES = 'PRESSURES'
const ENVIRONMENTAL_CONDITIONS = 'ENVIRONMENTAL_CONDITIONS'

const HydrobioOperationApp = () => {
    const dispatch = useDispatch()
    const { id: idStation, idOperation } = useParams()

    const {
        qualitometer,
        supports,
        hydrobioOperation,
        citiesIndex,
    } = useSelector(store => ({
        qualitometer: store.QualityReducer.qualitometer,
        supports: store.SupportReducer.supports,
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const supportFaunal = useSandreList('HYDROBIO_SUPPORT_FAUNISTIQUE')
    const supportFloristic = useSandreList('HYDROBIO_SUPPORT_FLORISTIQUE')

    const townName = useMemo(() => citiesIndex[qualitometer.townCode]?.name, [citiesIndex, qualitometer])

    useTitle(() => [{
        title: i18n.quality,
        href: 'quality',
    }, {
        title: getStationTitleWithTownName(qualitometer, townName),
        href: `station/quality/${idStation}`,
    }, {
        title: i18n.hydrobioOperations,
        href: `station/quality/${idStation}/hydrobioOperation`,
    }, {
        title: idOperation === 'new' ? idOperation : `${i18n.operation} : ${getDate(hydrobioOperation.date)} - ${getLabel(supports, hydrobioOperation.support)}`,
        href: `station/quality/${idStation}/hydrobioOperation/${idOperation}`,
    }], [qualitometer, hydrobioOperation, supports, townName])

    useEffect(() => {
        dispatch(MethodAction.fetchMethods())
        dispatch(QualityAction.fetchStatus())
        dispatch(QualityAction.fetchQualifications())
        dispatch(SupportAction.fetchSupports())
        dispatch(TaxonAction.fetchTaxons())
        dispatch(OperationAction.fetchRemarks())
        dispatch(SupportAction.fetchSupportThemes())
    }, [])

    useEffect(() => {
        dispatch(OperationAction.fetchQualitometerHydrobioOperation(idStation, idOperation))
        dispatch(OperationAction.fetchHydrobioList(idStation, idOperation))
        dispatch(OperationAction.fetchEnvironmentalConditions(idStation, idOperation))
        dispatch(OperationAction.fetchIndices(idStation, idOperation))
    }, [idStation, idOperation])

    return (
        <Grid style={{ padding: '10 10 100 20' }}>
            <Tabs
                defaultTab={DESCRIPTION}
                tabs={[
                    {
                        constant: DESCRIPTION,
                        label: i18n.description,
                    },
                    {
                        constant: ENVIRONMENTAL_CONDITIONS,
                        label: i18n.environmentalConditions,
                    },
                    {
                        constant: SAMPLE,
                        label: i18n.sample,
                        displayed: CODE_SUPPORT.includes(hydrobioOperation.support),
                    },
                    {
                        constant: SURVEY_UNITS,
                        label: i18n.relevedUnits,
                        displayed: hydrobioOperation.support === SUPPORT_MACROPHYTES,
                    },
                    {
                        constant: FAUNAL_LIST,
                        label: i18n.faunalList,
                        displayed: supportFaunal.some(s => s.name === `${hydrobioOperation.support}`),
                    },
                    {
                        constant: FLORISTIC_LIST,
                        label: i18n.floristicList,
                        displayed: supportFloristic.some(s => s.name === `${hydrobioOperation.support}`),
                    },
                    {
                        constant: INDICES,
                        label: i18n.indices,
                    },
                    {
                        constant: PRESSURES,
                        label: i18n.pressures,
                    },
                ]}
            >
                {activeTab => (
                    <>
                        {activeTab === DESCRIPTION && <HydrobioOperationDescription idStation={idStation} idOperation={idOperation} />}
                        {activeTab === ENVIRONMENTAL_CONDITIONS && <EnvironmentalConditionsPanel operation={hydrobioOperation} />}
                        {activeTab === SAMPLE && <HydrobioSample />}
                        {activeTab === SURVEY_UNITS && <HydrobioOperationSurveyUnits />}
                        {activeTab === FAUNAL_LIST && <HydrobioOperationFaunalList />}
                        {activeTab === FLORISTIC_LIST && <HydrobioOperationFloristicList />}
                        {activeTab === INDICES && (
                            <OperationIndicesPanel
                                qualitometerId={hydrobioOperation.qualitometer}
                                operationId={hydrobioOperation.id}
                                support={hydrobioOperation.support}
                            />
                        )}
                        {activeTab === PRESSURES && <OperationHydrobioPressuresPanel />}
                    </>
                )}
            </Tabs>
        </Grid>
    )
}

export default HydrobioOperationApp